import { useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Button } from '@aqarmap/aqarmap-ui';
import { useParams } from 'react-router-dom';
import { useAppDispatch } from '~/redux/hooks';
import useBreakpoint from '~/hooks/useBreakpoint';
import { setFooterDisplay } from '~/redux/features/uiState/uiStateSlice';
import { useFullRoutes, useQuery } from '~/hooks';
import { SelectedPaymentType } from '~/types/paymentTypes';
import PurchasePaymentMethod from '~/components/PurchasePaymentMethod';
import PaymentSummaryWrapper from '~/components/PaymentSummaryWrapper';
import Loader from '~/components/Loader';
import '~/cardsIcons.css';
import { RequestStatusEnum, RoutesEnum } from '~/constants/enums';
import {
  PaymentMethodEnum,
  PaymentOperationTypeEnum,
} from '~/constants/enums/paymentEnums';
import { UserCardFormHandle } from '~/components/UserCardForm/types';
import { usePostPaymentPayMutation } from '~/redux/api/PaymentApiSlice';
import {
  useGetListingDetailsQuery,
  useListingRulesQuery,
} from '~/redux/api/userApiSlice';
import { deleteListingData } from '~addListing/utils/LocalStorage';

export default function ListingCheckout() {
  const userCardFormRef = useRef<UserCardFormHandle>(null);
  const { id, locale } = useParams();
  const { ADD_LISTING_URL } = useFullRoutes();
  const dispatch = useAppDispatch();
  const { status } = useQuery();
  const [checkoutError, setCheckoutError] = useState<string>('');
  const { mobileScreen } = useBreakpoint();
  const { t } = useTranslation('app');
  const [selectedPayment, setSelectedPayment] = useState<
    SelectedPaymentType | undefined
  >(undefined);
  const [isPayfortLoading, setIsPayfortLoading] = useState<boolean>(false);

  const { data: listingRulesData, isLoading: isLoadingListingFees } =
    useListingRulesQuery(id);
  const {
    data: listingDetailsData,
    isFetching: isFetchingListingDetails,
    isError: isListingDetailsError,
  } = useGetListingDetailsQuery(id);
  const [postPaymentPay, { isLoading: isPayLoading }] =
    usePostPaymentPayMutation();

  const totalAmount = listingRulesData ? listingRulesData.total_amount : '';
  const slug = listingDetailsData?.listing?.section?.slug;
  // Tolerate a missing slug so the redirect target is always valid even if the
  // listing-details query fails on the post-payment page reload.
  const listingDetailUrl = `${process.env.REACT_APP_BASE_URL}/${locale}/${RoutesEnum.LISTING}/${id}${slug ? `-${slug}` : ''}`;
  const isSuccessReturn = status === RequestStatusEnum.SUCCESS;

  const handlePayWithSavedCard = async () => {
    if (!selectedPayment) return;
    const response = await postPaymentPay({
      paymentMethod: PaymentMethodEnum.SAVED_CARD,
      cardId: selectedPayment.id,
      operationType: PaymentOperationTypeEnum.PAY_AS_YOU_GO_CREDIT,
      listingId: id,
    });
    const { data, error } = response;
    if (data?.status === 'success') {
      deleteListingData(`${id}`);
      window.location.href = listingDetailUrl;
    } else if (data?.message) {
      setCheckoutError(t(data?.message));
    } else if (error) {
      setCheckoutError(
        t(
          (error as { data?: { message?: string } })?.data?.message ||
            'somethingWrong',
        ),
      );
    } else {
      setCheckoutError(t('somethingWrong'));
    }
  };

  const onCheckoutSubmit = () => {
    const isNewCard = selectedPayment?.isNew || false;
    const isValidUserCardForm = userCardFormRef?.current;
    if (isNewCard && isValidUserCardForm) {
      setIsPayfortLoading(true);
      userCardFormRef.current.submitUserCard({
        operationType: PaymentOperationTypeEnum.PAY_AS_YOU_GO_CREDIT,
        listingId: id,
        returnTo: `${process.env.REACT_APP_BASE_URL}${ADD_LISTING_URL.CHECKOUT(`${id}`)}`,
      });
    } else {
      handlePayWithSavedCard();
    }
  };

  useEffect(() => {
    if (status === RequestStatusEnum.FAILED) {
      setCheckoutError(t('somethingWrong'));
      setSelectedPayment({ isNew: true, id: 0 });
    }
  }, [status]);

  // After a successful new-card payment, Payfort reloads this page with
  // ?status=success. Redirect to the listing as soon as the details query
  // settles (with or without a slug) so a charged user is never stranded on
  // the payment form — even if that query errors.
  useEffect(() => {
    if (!isSuccessReturn) return;
    if (
      !isFetchingListingDetails &&
      (listingDetailsData || isListingDetailsError)
    ) {
      deleteListingData(`${id}`);
      window.location.href = listingDetailUrl;
    }
  }, [
    isSuccessReturn,
    isFetchingListingDetails,
    listingDetailsData,
    isListingDetailsError,
  ]);

  useEffect(() => {
    if (mobileScreen) {
      dispatch(setFooterDisplay('hide'));
    }
  }, [mobileScreen]);

  useEffect(() => {
    document.title = `${t('payNow')}|${t('aqarmap')}`;
  }, []);

  // Keep the loader mounted on the success return so the payment form never
  // reappears for an already-charged listing.
  if (isLoadingListingFees || isFetchingListingDetails || isSuccessReturn)
    return <Loader />;

  const isSubmitDisabled = !selectedPayment || isPayLoading || isPayfortLoading;

  return (
    <div className="layout-boundary pt-4x pb-[40px] md:pb-[80px]">
      <div className="layout-boundary__inner flex flex-col-reverse md:flex-row gap-4x justify-between px-2x md:px-0">
        <PurchasePaymentMethod
          title={t('topupDetails')}
          setSelectedPayment={setSelectedPayment}
          selectedPayment={selectedPayment}
          error={checkoutError}
          newCardProps={{
            onFailure: () => {
              setIsPayfortLoading(false);
              setCheckoutError(t('somethingWrong'));
            },
            ref: userCardFormRef,
          }}
        />
        <PaymentSummaryWrapper
          totalFeesLabel={t('totalFees')}
          submitBtnLabel={t('payAndPublishListing')}
          submitBtnProps={{
            onClick: onCheckoutSubmit,
            isLoading: isPayLoading || isPayfortLoading,
            disabled: isSubmitDisabled,
          }}
          fees={
            <Trans
              i18nKey="priceValue"
              t={t}
              values={{
                value: totalAmount,
              }}
              components={{
                tag: <span>placeholder</span>,
              }}
            />
          }
          summary={
            <div className="flex justify-between">
              <p className="text-body_1 text-gray__dark_2">
                {t('listingCost')}
              </p>
              <p className="text-title_4 text-gray__dark_2">
                <Trans
                  i18nKey="priceValue"
                  t={t}
                  values={{
                    value: totalAmount,
                  }}
                  components={{
                    tag: <span>placeholder</span>,
                  }}
                />
              </p>
            </div>
          }
          title={t('topupSummary')}
        />
        <div className="flex z-[40] md:hidden justify-between items-center  fixed bottom-0 left-0 w-full pt-2x pb-3x bg-white px-2x  border-t border-gray">
          <Button
            size="medium"
            variant="primary"
            className="w-full md:hidden"
            onClick={onCheckoutSubmit}
            isLoading={isPayLoading || isPayfortLoading}
            disabled={isSubmitDisabled}
          >
            {t('payAndPublishListing')}
          </Button>
        </div>
      </div>
    </div>
  );
}
